/**
 * Shape Duffel Order (post-booking) for the confirmation / thank-you UI.
 * Field names follow Duffel API v2; missing fields are handled defensively.
 */

/** Remove zero-width / BOM so strings don't wrap one character per line in PDF / odd UI. */
export function normalizeDuffelDisplayText(value) {
  if (value == null || value === '') return '';
  const s = typeof value === 'string' ? value : String(value);
  return s
    .normalize('NFC')
    .replace(/[\uFEFF\u200B-\u200F\u2060]/g, '')
    .replace(/\u00A0/g, ' ')
    .trim();
}

function sliceTitle(sliceCount, index) {
  if (sliceCount <= 1) return 'Flight';
  if (index === 0) return 'Outbound';
  if (index === 1) return 'Return';
  return `Leg ${index + 1}`;
}

export function summarizeConfirmedFlightOrder(order) {
  if (!order || typeof order !== 'object') return null;

  const airlineRefs = [];
  for (const r of order.booking_references || []) {
    const ref = r.reference || r.airline_reference;
    if (!ref) continue;
    const label =
      r.airline_name ||
      r.carrier?.name ||
      [r.carrier?.iata_code].filter(Boolean).join(' ') ||
      'Airline';
    airlineRefs.push({
      label: normalizeDuffelDisplayText(label),
      reference: normalizeDuffelDisplayText(String(ref)),
    });
  }

  const passengersById = new Map(
    (order.passengers || []).map((p) => [p.id, p]),
  );

  const ticketDocuments = [];
  for (const d of order.documents || []) {
    const rawType = d.type || 'document';
    const typeLabel = String(rawType).replace(/_/g, ' ');
    const uid =
      d.unique_identifier != null && d.unique_identifier !== ''
        ? String(d.unique_identifier).trim()
        : '';
    const docId = d.id ? String(d.id) : '';

    const pid =
      Array.isArray(d.passenger_ids) && d.passenger_ids.length
        ? d.passenger_ids[0]
        : d.passenger_id;
    let passengerLabel = '';
    if (pid) {
      const pax = passengersById.get(pid);
      if (pax) {
        passengerLabel =
          [pax.title, pax.given_name, pax.family_name].filter(Boolean).join(' ').trim() ||
          String(pid);
      } else {
        passengerLabel = String(pid);
      }
    }

    const ticketNumber = normalizeDuffelDisplayText(uid || docId);
    let displayLabel = '';
    if (uid && (!/^\d{1,3}$/.test(uid) || uid.length >= 6)) {
      displayLabel = `E-ticket / ticket no.: ${normalizeDuffelDisplayText(uid)}`;
    } else if (uid) {
      displayLabel = `Ticket reference (carrier): ${normalizeDuffelDisplayText(uid)}`;
    } else if (docId) {
      displayLabel = `Ticket document: ${normalizeDuffelDisplayText(docId)}`;
    } else {
      displayLabel = normalizeDuffelDisplayText(typeLabel);
    }

    ticketDocuments.push({
      ticketNumber,
      type: normalizeDuffelDisplayText(typeLabel),
      documentId: normalizeDuffelDisplayText(docId),
      passengerLabel: normalizeDuffelDisplayText(passengerLabel),
      displayLabel,
    });
  }

  const itinerary = [];
  const slices = order.slices || [];
  slices.forEach((slice, si) => {
    (slice.segments || []).forEach((seg, gi) => {
      const mkt = seg.marketing_carrier || seg.operating_carrier;
      const fnRaw =
        seg.marketing_carrier_flight_number ||
        seg.operating_carrier_flight_number ||
        seg.flight_number ||
        '';
      const fn = fnRaw != null && fnRaw !== '' ? String(fnRaw) : '';
      const iata = mkt?.iata_code ? String(mkt.iata_code) : '';
      const flightNumRaw =
        iata && fn ? `${iata} ${fn}`.trim() : [iata, fn].filter(Boolean).join(' ').trim();
      const flightNum = normalizeDuffelDisplayText(flightNumRaw || '-');

      itinerary.push({
        id: seg.id || `${si}-${gi}`,
        sliceLabel: sliceTitle(slices.length, si),
        carrier: normalizeDuffelDisplayText(mkt?.name || ''),
        flightNum,
        originCode: normalizeDuffelDisplayText(
          seg.origin?.iata_code || seg.origin?.city?.iata_code || '-',
        ),
        originName: normalizeDuffelDisplayText(
          seg.origin?.name || seg.origin?.city?.name || '',
        ),
        destCode: normalizeDuffelDisplayText(
          seg.destination?.iata_code || seg.destination?.city?.iata_code || '-',
        ),
        destName: normalizeDuffelDisplayText(
          seg.destination?.name || seg.destination?.city?.name || '',
        ),
        depart: seg.departing_at || '',
        arrive: seg.arriving_at || '',
      });
    });
  });

  const passengers = (order.passengers || []).map((p) => ({
    id: p.id,
    displayName: normalizeDuffelDisplayText(
      [p.title, p.given_name, p.family_name].filter(Boolean).join(' ').trim() || 'Traveler',
    ),
    email: normalizeDuffelDisplayText(p.email || ''),
    passengerType: normalizeDuffelDisplayText(p.type || ''),
  }));

  return {
    duffelReference: normalizeDuffelDisplayText(order.booking_reference || ''),
    airlineRefs,
    ticketDocuments,
    itinerary,
    passengers,
    totalAmount: order.total_amount,
    totalCurrency: normalizeDuffelDisplayText(order.total_currency || ''),
    validatingCarrier: normalizeDuffelDisplayText(order.owner?.name || ''),
  };
}
